import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, query, orderBy, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { useLocation, Link } from 'react-router-dom';
import ImageModal from '../components/ImageModal'; 
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { ToastContainer, toast } from 'react-toastify';
import '../style/products.css';
import { FaTrash, FaShoppingCart } from 'react-icons/fa';
import axios from 'axios';

const Products = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  // const shopId = queryParams.get('shop');

  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Search and load more state
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleDesigns, setVisibleDesigns] = useState(7);
  const designsPerLoad = 7;
  
  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [designToDelete, setDesignToDelete] = useState(null);
  const [showPushModal, setShowPushModal] = useState(false);
  const [designToPush, setDesignToPush] = useState(null);
  
  // Push to store state
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const [pushStatus, setPushStatus] = useState(''); // 'success' or 'error'

  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not logged in");
  }

  useEffect(() => {
    const fetchDesigns = async () => {
      try {
        const designsCollection = collection(db, 'users', user.uid, 'designs');
        const q = query(designsCollection, orderBy('timestamp', 'desc'));
        const designSnapshot = await getDocs(q);
        const designList = designSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setDesigns(designList);
      } catch (error) {
        console.error("Error fetching designs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
  }, []);

  // Helper function to determine product type
  const getProductType = (design) => {
    return design.productType || 'tshirt'; // Default to tshirt if not specified
  };

  // Helper function to get print costs based on product type
  const getPrintCosts = (design) => {
    const productType = getProductType(design);
    
    if (productType === 'joggers') {
      return {
        front: design.PrintCost474 || 0,
        back: design.PrintCost474 || 0,
        total: design.PrintCost474 || 0
      };
    } else {
      // T-shirt format
      return {
        front: design.costs?.front || 0,
        back: design.costs?.back || 0,
        total: (design.costs?.front || 0) + (design.costs?.back || 0)
      };
    }
  };

  // Helper function to get base cost
  const getBaseCost = (design) => {
    const productType = getProductType(design);
    
    if (productType === 'joggers') {
      return design.baseCost570 || 0;
    } else {
      return design.baseCost || 0;
    }
  };

  // Helper function to get product specifications
  const getProductSpecs = (design) => {
    const productType = getProductType(design);
    
    if (productType === 'joggers') {
      return design.productGSM || 'N/A';
    } else {
      return design.productSpecs || 'N/A';
    }
  };

  // Filter designs based on search term
  const filteredDesigns = designs.filter(design => 
    design.designName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Get the currently visible designs
  const currentDesigns = filteredDesigns.slice(0, visibleDesigns);

  // Handle load more button click
  const handleLoadMore = () => {
    setVisibleDesigns(prev => prev + designsPerLoad);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setVisibleDesigns(designsPerLoad); // Reset to initial load amount when searching
  };

  const handleImageClick = (imageSrc) => {
    setSelectedImage(imageSrc); // Set the clicked image as the selected one
  };

  const handleCloseModal = () => {
    setSelectedImage(null); // Close the modal by clearing the selected image
  };

  const handleDeleteClick = (designId) => {
    setDesignToDelete(designId); // Set the design ID to be deleted
    setShowDeleteModal(true); // Show the confirmation modal
  };

  const handleDelete = async () => {
    try {
      const designDoc = doc(db, 'users', user.uid, 'designs', designToDelete);
      await deleteDoc(designDoc);
      setDesigns(designs.filter(design => design.id !== designToDelete)); // Remove deleted design from the UI
    } catch (error) {
      console.error("Error deleting design:", error);
    } finally {
      setShowDeleteModal(false); // Close the modal after deletion
      setDesignToDelete(null); // Clear the design ID
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false); // Close the modal if the user cancels
    setDesignToDelete(null); // Clear the design ID
  };

  // Push to Store functionality
  const handlePushToStoreClick = (design) => {
    setDesignToPush(design);
    setShowPushModal(true);
    // Reset status messages
    setPushMessage('');
    setPushStatus('');
  };

  const handleCancelPush = () => {
    setShowPushModal(false);
    setDesignToPush(null);
  };

  const handlePushToStore = async () => {
    setPushLoading(true);
    setPushMessage('Pushing product to Shopify store...');
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
  
      if (!userDoc.exists()) {
        return;
      }
  
      const userData = userDoc.data();
      const userShop = userData.shop;

      const payload = {
        productName: designToPush.productName,
        frontImage: designToPush.images.front,
        backImage: designToPush.images.back,
        shop: userShop,
        designName: designToPush.designName,
        description: designToPush.description,
        sellingPrice: designToPush.sellingPrice,
        SKUs: designToPush.SKUs,
        status: 'ACTIVE', // Default to active
        userId: user.uid,
        productType: getProductType(designToPush) // Include product type
      };

      const response = await axios.post('https://ufb-1.onrender.com/push-to-store', payload);
      
      setPushStatus('success');
      setPushMessage(`Product successfully pushed to Shopify! Product ID: ${response.data.productId}`);
      toast.success("Product is live on Shopify!");
    } catch (error) {
      console.error('Error pushing to store:', error);
      setPushStatus('error');
      setPushMessage(`Error pushing to store: ${error.response?.data?.message || error.message}`);
    } finally {
      setPushLoading(false);
      setShowPushModal(false);
    }
  };

  // Helper function to format size objects
  const formatSize = (sizeObj) => {
    if (!sizeObj || typeof sizeObj !== 'object') return 'N/A';
    const width = Math.floor(sizeObj.width) || 0;
    const height = Math.floor(sizeObj.height) || 0;
    return `${width}×${height}`;
  };

  const getPrintSizes = (design) => {
    const productType = getProductType(design);
    const printSizes = design.Printsizes || {};
    
    if (productType === 'joggers') {
      // For joggers, print sizes are stored in a detailed map with individual print areas
      const frontSizes = printSizes.front || {};
      const backSizes = printSizes.back || {};
      
      return {
        type: 'joggers',
        front: {
          frontLeftFull: frontSizes.frontLeftFull || { height: 0, width: 0 },
          frontLeftLogo: frontSizes.frontLeftLogo || { height: 0, width: 0 },
          frontRightFull: frontSizes.frontRightFull || { height: 0, width: 0 },
          frontRightLogo: frontSizes.frontRightLogo || { height: 0, width: 0 }
        },
        back: {
          backBumArea: backSizes.backBumArea || { height: 0, width: 0 },
          backLeftFull: backSizes.backLeftFull || { height: 0, width: 0 },
          backRightFull: backSizes.backRightFull || { height: 0, width: 0 }
        }
      };
    } else {
      // Regular t-shirt format
      return {
        type: 'regular',
        front: printSizes.front || { height: 0, width: 0 },
        back: printSizes.back || { height: 0, width: 0 }
      };
    }
  };

  if (loading) {
    return <div className="loading-message">Loading...</div>;
  }

  return (
    <div className="products-page">
      <Header />
      <div className="products-container">
        <Sidebar />
        <div className="main-content">
          <div className="actions-container">
            <Link to={'/selecttshirt'} className="create-button">Create Product</Link>
            <div className="search-container">
              <input
                type="text"
                placeholder="Search by design name..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
            </div>
          </div>
          
          {designs.length === 0 ? (
            <p>No designs uploaded yet for this shop.</p>
          ) : filteredDesigns.length === 0 ? (
            <p>No designs match your search.</p>
          ) : (
            <>
              <table className="design-table">
                <thead>
                  <tr>
                    <th>Design Name</th>
                    <th>Product Name</th>
                    <th>Product Type</th>
                    <th>Print Method</th>
                    <th>Front Mockup</th>
                    <th>Back Mockup</th>
                    <th>Print Cost</th>
                    <th>Print Sizes</th>
                    <th>Base Cost</th>
                    <th>Selling Price</th>
                    <th>Total Price</th>
                    <th>Product GSM</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentDesigns.map(design => {
                    const productType = getProductType(design);
                    const printCosts = getPrintCosts(design);
                    const printSizes = getPrintSizes(design);
                    const baseCost = getBaseCost(design);
                    const productSpecs = getProductSpecs(design);
                    
                    return (
                      <tr key={design.id}>
                        <td>{design.designName}</td>
                        <td>{design.productName}</td>
                        <td>
                          <span className={`product-type-badge ${productType}`}>
                            {productType.charAt(0).toUpperCase() + productType.slice(1)}
                          </span>
                        </td>
                        {/* <td>{design.printMethod}</td> */}

                        <td>
  {typeof design.printMethod === 'object' && design.printMethod !== null
    ? Object.entries(design.printMethod)
        .filter(([key, value]) => value !== null && value !== undefined)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')
    : design.printMethod
  }
</td>
                        <td>
                          <img 
                            src={design.images.front} 
                            alt='Front Design' 
                            style={{ width: '50px', height: 'auto', cursor: 'pointer' }} 
                            onClick={() => handleImageClick(design.images.front)}
                          />
                        </td>
                        <td>
                          <img 
                            src={design.images.back} 
                            alt="Back Design" 
                            style={{ width: '50px', height: 'auto', cursor: 'pointer' }} 
                            onClick={() => handleImageClick(design.images.back)}
                          />
                        </td>
                        <td>{design.PrintCost}</td>
                        <td>
                          {printSizes.type === 'joggers' ? (
                            <div>
                              <div>
                                Front Sizes:
                              </div>
                              <div>
                                • L Full: {formatSize(printSizes.front.frontLeftFull)}
                              </div>
                              <div>
                                • L Logo: {formatSize(printSizes.front.frontLeftLogo)}
                              </div>
                              <div >
                                • R Full: {formatSize(printSizes.front.frontRightFull)}
                              </div>
                              <div>
                                • R Logo: {formatSize(printSizes.front.frontRightLogo)}
                              </div>
                              
                              <div>
                                Back Sizes:
                              </div>
                              <div>
                                • Bum: {formatSize(printSizes.back.backBumArea)}
                              </div>
                              <div>
                                • L Full: {formatSize(printSizes.back.backLeftFull)}
                              </div>
                              <div >
                                • R Full: {formatSize(printSizes.back.backRightFull)}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div>Front: {formatSize(printSizes.front)}</div>
                              <div>Back: {formatSize(printSizes.back)}</div>
                            </div>
                          )}
                        </td>
                        <td>{design.baseCost}</td>
                        <td>{design.sellingPrice}</td>
                        <td>{design.totalPrice}</td>
                        <td>{design.productGSM}</td>
                        <td className="action-buttons">
                          <button 
                            onClick={() => handlePushToStoreClick(design)} 
                            className="push-button"
                            title="Push to Shopify Store"
                            style={{ marginBottom: '5px' }}
                          >
                            <FaShoppingCart />
                          </button>
                          <button 
                            onClick={() => handleDeleteClick(design.id)} 
                            className="delete-button"
                            title="Delete Product"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              
              {/* Load More Button */}
              {visibleDesigns < filteredDesigns.length && (
                <div className="load-more-container">
                  <button onClick={handleLoadMore} className="load-more-button">
                    Load More
                  </button>
                </div>
              )}
              
              <ImageModal imageSrc={selectedImage} onClose={handleCloseModal} />
            </>
          )}
        </div>
      </div>

      {/* Confirmation Modal for Deleting */}
      {showDeleteModal && (
        <div className="modals">
          <div className="modal-contents">
            <h3>Are you sure you want to delete this design?</h3>
            <button onClick={handleDelete} className="yes-button">Yes</button>
            <button onClick={handleCancelDelete} className="no-button">No</button>
          </div>
        </div>
      )}

      {/* Push to Store Modal */}
      {showPushModal && designToPush && (
        <div className="modals">
          <div className="modal-contents push-modal">
            <h3>Push Product to Shopify Store</h3>
            <p>Are you sure you want to push "{designToPush.designName}" to your Shopify store?</p>
            
            {pushMessage && (
              <div className={`push-message ${pushStatus}`}>
                {pushMessage}
              </div>
            )}
            
            <div className="modal-buttons">
              <button 
                onClick={handlePushToStore} 
                className="yes-button"
                disabled={pushLoading || pushStatus === 'success'}
              >
                {pushLoading ? 'Pushing...' : 'Yes, Push to Store'}
              </button>
              <button 
                onClick={handleCancelPush} 
                className="no-button"
                disabled={pushLoading}
              >
                {pushStatus === 'success' ? 'Close' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;